import {
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { prefersReducedMotion } from '../../../../core/utils/motion.util';
import { PERSONAS } from '../../data/landing-content';

@Component({
  selector: 'app-personalization-section',
  standalone: true,
  template: `
    <section class="landing-section compact">
      <div class="landing-inner">
        <div class="reveal">
          <div class="landing-kicker">
            <span class="brush"></span>True personalization
          </div>
          <h2 class="landing-h2 wide">
            A path that <span class="accent">grows with you.</span>
          </h2>
          <p class="landing-sub wide">
            KΛI doesn't enroll you in a course. It maps your arc toward your
            goal — shaping every step around your journey and how you actually
            learn.
          </p>
        </div>

        <div class="carve-grid reveal" data-delay="1" #carveGrid>
          <div>
            <p class="persona-label">Watch KΛI personalize — pick a path</p>
            <div class="personas">
              @for (persona of personas; track persona.to; let i = $index) {
                <button
                  class="persona"
                  [class.active]="activeIndex() === i"
                  (click)="selectPersona(i, true)"
                >
                  <span class="p-kanji">{{ persona.kanji }}</span>
                  <span>
                    <span class="p-from">{{ persona.from }}</span>
                    <span class="p-connector">↓</span>
                    <span class="p-to">{{ persona.to }}</span>
                  </span>
                </button>
              }
            </div>
            <p class="carve-summary" [style.opacity]="summaryVisible() ? 1 : 0">
              {{ activePersona().summary[0]
              }}<span class="num">{{ activePersona().summary[1] }}</span
              >{{ activePersona().summary[2] }}
            </p>
          </div>

          <div class="syllabus-card">
            <div class="arc-head">
              <span class="arc-label">Your arc</span>
              <span class="arc-title">
                {{ activePersona().from }}
                <span class="arrow">→</span>
                {{ activePersona().to }}
              </span>
            </div>

            <div class="traj">
              @for (
                step of activePersona().steps;
                track step.title;
                let i = $index
              ) {
                <div
                  class="tr-stage"
                  [class.lit]="litSteps().has(i)"
                  [class.drawn]="drawnSteps().has(i)"
                >
                  <div class="tr-rail">
                    <span class="tr-dot"></span>
                    @if (i < activePersona().steps.length - 1) {
                      <span class="tr-line"></span>
                    }
                  </div>
                  <div class="tr-body">
                    <span class="tr-label">Step {{ i + 1 }}</span>
                    <span class="tr-title">{{ step.title }}</span>
                    <span class="tr-desc">{{ step.description }}</span>
                  </div>
                </div>
              }
            </div>

            <div class="sy-foot">
              <span class="kai-tag">KΛI</span>
              <span
                class="nudge-text"
                [style.opacity]="nudgeVisible() ? 1 : 0"
                >{{ activePersona().nudge }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styleUrl: './personalization-section.component.scss',
})
export class PersonalizationSectionComponent {
  private readonly carveGrid = viewChild<ElementRef<HTMLElement>>('carveGrid');
  private readonly destroyRef = inject(DestroyRef);

  readonly personas = PERSONAS;
  readonly activeIndex = signal(0);
  readonly litSteps = signal(new Set<number>());
  readonly drawnSteps = signal(new Set<number>());
  readonly nudgeVisible = signal(true);
  readonly summaryVisible = signal(true);

  readonly activePersona = signal(PERSONAS[0]);

  private carveTimers: ReturnType<typeof setTimeout>[] = [];
  private autoTimer: ReturnType<typeof setInterval> | null = null;
  private userTookOver = false;

  constructor() {
    afterNextRender(() => {
      const grid = this.carveGrid()?.nativeElement;
      if (!grid) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.selectPersona(0, false);

            if (!prefersReducedMotion()) {
              this.autoTimer = setInterval(() => {
                if (!this.userTookOver) {
                  this.selectPersona(
                    (this.activeIndex() + 1) % this.personas.length,
                    false,
                  );
                }
              }, 5600);
            }

            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.35 },
      );

      observer.observe(grid);
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        this.clearCarveTimers();
        if (this.autoTimer) clearInterval(this.autoTimer);
      });
    });
  }

  selectPersona(index: number, userInitiated: boolean): void {
    if (userInitiated) {
      this.userTookOver = true;
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    }

    this.activeIndex.set(index);
    this.activePersona.set(this.personas[index]);
    this.carveArc();
  }

  private carveArc(): void {
    this.clearCarveTimers();
    this.litSteps.set(new Set());
    this.drawnSteps.set(new Set());
    this.nudgeVisible.set(false);
    this.summaryVisible.set(false);

    const stepCount = this.activePersona().steps.length;
    const reduced = prefersReducedMotion();
    const baseDelay = reduced ? 0 : 350;
    const stepDelay = reduced ? 0 : 520;

    for (let i = 0; i < stepCount; i++) {
      this.carveTimers.push(
        setTimeout(
          () => {
            this.litSteps.update((set) => new Set(set).add(i));
            this.carveTimers.push(
              setTimeout(
                () => this.drawnSteps.update((set) => new Set(set).add(i)),
                reduced ? 0 : 300,
              ),
            );
          },
          baseDelay + i * stepDelay,
        ),
      );
    }

    this.carveTimers.push(
      setTimeout(
        () => {
          this.nudgeVisible.set(true);
          this.summaryVisible.set(true);
        },
        baseDelay + stepCount * stepDelay + (reduced ? 0 : 250),
      ),
    );
  }

  private clearCarveTimers(): void {
    this.carveTimers.forEach(clearTimeout);
    this.carveTimers = [];
  }
}
